import React, { createContext, useRef, useState } from 'react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { HBSegmentedControlProps } from './HBSegmentedControl.types';
import { TControlSize } from '../../tokens/types';
import { Container } from './HBSegmentedControl.styles';
import HBSegmentedControlOption from './HBSegmentedControlOption';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { guid } from '../../utils/shared.utils';
import { ITooltipProps } from '../HBTooltip/HBTooltip';

interface SegmentedControlContextValue {
    selectedValue: string | undefined;
    size: TControlSize;
    disabled: boolean;
    stretchToFill: boolean;
    layoutId: string;
    tooltipConfig?: Omit<ITooltipProps, 'target' | 'children'>;
    hoveredValue: string | undefined;
    setHoveredValue: (value: string | undefined) => void;
}

export const SegmentedControlContext = createContext<
    SegmentedControlContextValue | undefined
>(undefined);

const HBSegmentedControlBase: React.FC<HBSegmentedControlProps> = ({
    value,
    onChange,
    size = 'md',
    stretchToFill = false,
    disabled = false,
    testId,
    testIdName,
    children,
    tooltipConfig,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy
}) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'segmented-control',
        componentName: 'HBSegmentedControl'
    });

    const { current: layoutId } = useRef(
        `segmented-control-indicator-${guid()}`
    );

    const [hoveredValue, setHoveredValue] = useState<string | undefined>();

    const handleValueChange = (newValue: string) => {
        // Only update if there's a new value (prevent unselecting)
        if (newValue) {
            onChange?.(newValue);
        }
    };

    const contextValue: SegmentedControlContextValue = {
        selectedValue: value,
        size,
        disabled,
        stretchToFill,
        layoutId,
        tooltipConfig,
        hoveredValue,
        setHoveredValue
    };

    return (
        <SegmentedControlContext.Provider value={contextValue}>
            <ToggleGroup.Root
                type="single"
                value={value || undefined}
                onValueChange={handleValueChange}
                disabled={disabled}
                id={id}
                aria-label={ariaLabelledBy ? undefined : ariaLabel}
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                asChild
            >
                <Container
                    $stretchToFill={stretchToFill}
                    $size={size}
                    {...(finalTestId && { 'data-testid': finalTestId })}
                >
                    {children}
                </Container>
            </ToggleGroup.Root>
        </SegmentedControlContext.Provider>
    );
};

// Attach Option as a static property for compound component pattern
export const HBSegmentedControl = Object.assign(HBSegmentedControlBase, {
    Option: HBSegmentedControlOption
});
